import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type SVGProps,
} from "react";
import { motion, useReducedMotion } from "framer-motion";
import type {
  AssistantActionBase,
  AssistantActiveRouteContextEnvelope,
  AssistantFeedback,
  AssistantMessage,
  AssistantPresentationModel,
  AssistantOpenArtifactInGoogleSheetsHandler,
  AssistantPolicyEventHandler,
} from "../types";
import {
  formatMessage,
  type MessageFormatStyles,
} from "../utils/messageFormat";
import {
  resolveAssistantPresentation,
  sanitizeVisibleAssistantText,
} from "../utils/assistantPresentation";
import {
  collectAssistantPresentationRevealText,
  resolveAssistantTextRevealConfig,
  type ResolvedAssistantTextRevealConfig,
} from "../utils/assistantResponseRenderPolicy";
import styles from "../styles/AIAssistant.module.css";
import {
  Badge,
  Group,
  Paper,
  Stack,
  Tooltip,
  ActionIcon,
} from "../ui/primitives.js";
import { AssistantPresentationRenderer } from "./AssistantPresentationRenderer";
import { PrototypeMinimalPresentationRenderer } from "./PrototypeMinimalPresentationRenderer";

const settledTextRevealMessageIds = new Set<string>();

type AssistantTablerIconProps = SVGProps<SVGSVGElement> & {
  size?: number;
};

const AssistantTablerIcon = ({
  children,
  size = 16,
  ...props
}: AssistantTablerIconProps) => (
  <svg
    aria-hidden="true"
    fill="none"
    height={size}
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth={2}
    viewBox="0 0 24 24"
    width={size}
    {...props}
  >
    {children}
  </svg>
);

const AssistantCopyIcon = (props: AssistantTablerIconProps) => (
  <AssistantTablerIcon {...props}>
    <path d="M8 8m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z" />
    <path d="M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2" />
  </AssistantTablerIcon>
);

const AssistantFileTextIcon = (props: AssistantTablerIconProps) => (
  <AssistantTablerIcon {...props}>
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
    <path d="M9 9h1" />
    <path d="M9 13h6" />
    <path d="M9 17h6" />
  </AssistantTablerIcon>
);

const AssistantRefreshIcon = (props: AssistantTablerIconProps) => (
  <AssistantTablerIcon {...props}>
    <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
    <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
  </AssistantTablerIcon>
);

const AssistantThumbUpIcon = (props: AssistantTablerIconProps) => (
  <AssistantTablerIcon {...props}>
    <path d="M7 11v8a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1v-7a1 1 0 0 1 1 -1h3" />
    <path d="M7 11l3 -7a2 2 0 0 1 2 2v4h5.5a2 2 0 0 1 1.92 2.56l-2.33 6.44a2 2 0 0 1 -1.92 1.44h-5.17a3 3 0 0 1 -3 -3v-7z" />
  </AssistantTablerIcon>
);

const AssistantThumbDownIcon = (props: AssistantTablerIconProps) => (
  <AssistantTablerIcon {...props}>
    <path d="M7 13v-8a1 1 0 0 0 -1 -1h-2a1 1 0 0 0 -1 1v7a1 1 0 0 0 1 1h3" />
    <path d="M7 13l3 7a2 2 0 0 0 2 -2v-4h5.5a2 2 0 0 0 1.92 -2.56l-2.33 -6.44a2 2 0 0 0 -1.92 -1.44h-5.17a3 3 0 0 0 -3 3v7z" />
  </AssistantTablerIcon>
);

export type AIAssistantMessageProps<
  TCitation,
  TAction extends AssistantActionBase,
> = {
  message: AssistantMessage<TCitation, TAction>;
  onCitationClick?: (citation: TCitation) => void;
  getCitationLabel?: (citation: TCitation, index: number) => string;
  getCitationTooltip?: (
    citation: TCitation,
    index: number,
  ) => string | undefined;
  isPending?: boolean;
  feedback?: AssistantFeedback;
  allowRetry?: boolean;
  onRetry?: (messageId: string) => void;
  onFeedback?: (messageId: string, feedback: AssistantFeedback) => void;
  onAction?: (action: TAction) => void;
  onTaskProposalStart?: (proposalId: string) => void;
  onTaskProposalAskInChat?: (proposalId: string) => void;
  onTaskProposalDismiss?: (proposalId: string) => void;
  activeRouteContext?: AssistantActiveRouteContextEnvelope | null;
  onAssistantPolicyEvent?: AssistantPolicyEventHandler;
  onOpenArtifactInGoogleSheets?: AssistantOpenArtifactInGoogleSheetsHandler;
  showCopy?: boolean;
  showFeedback?: boolean;
  showRetry?: boolean;
  onRevealProgress?: () => void;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const tokenizeRevealText = (value: string): string[] => {
  const matches = value.match(/\S+\s*|\s+/g);
  return matches ? matches.filter((token) => token.length > 0) : [];
};

const resolveTextRevealMessageId = (messageId: unknown): string | null => {
  const normalized = String(messageId ?? "").trim();
  return normalized ? normalized : null;
};

const SUMMARY_ONLY_PROTOTYPE_SECTION_TYPES = new Set([
  "summary",
  "formatted_body",
  "sources",
]);

const hasStructuredPresentationSections = (
  presentation: AssistantPresentationModel | null,
): boolean =>
  Boolean(
    presentation?.sections.some(
      (section) => !SUMMARY_ONLY_PROTOTYPE_SECTION_TYPES.has(section.type),
    ),
  );

const shouldUsePrototypeExactRenderer = (
  presentation: AssistantPresentationModel | null,
  metadata: unknown,
): boolean =>
  Boolean(presentation) &&
  isRecord(metadata) &&
  metadata.presentationChrome === "prototype-minimal" &&
  metadata.presentationVariant === "prototype-exact" &&
  !hasStructuredPresentationSections(presentation);

export function AIAssistantMessage<
  TCitation,
  TAction extends AssistantActionBase,
>({
  message,
  onCitationClick,
  getCitationLabel,
  getCitationTooltip,
  isPending = false,
  feedback,
  allowRetry = true,
  onRetry,
  onFeedback,
  onAction,
  onTaskProposalStart,
  onTaskProposalAskInChat,
  onTaskProposalDismiss,
  activeRouteContext,
  onAssistantPolicyEvent,
  onOpenArtifactInGoogleSheets,
  showCopy = true,
  showFeedback = true,
  showRetry = true,
  onRevealProgress,
}: AIAssistantMessageProps<TCitation, TAction>) {
  const { role, content, citations = [], actions = [] } = message;
  const isUser = role === "user";
  const visibleContent = useMemo(
    () => (isUser ? content : sanitizeVisibleAssistantText(content)),
    [content, isUser],
  );
  const [copied, setCopied] = useState(false);
  const [feedbackBurstKey, setFeedbackBurstKey] = useState(0);
  const reducedMotion = Boolean(useReducedMotion());
  const revealTimerRef = useRef<number | null>(null);
  const revealStartedRef = useRef(false);
  const [revealPhase, setRevealPhase] = useState<
    "revealing" | "handoff" | "settled"
  >("settled");
  const [revealedTokenCount, setRevealedTokenCount] = useState(0);
  const onRevealProgressRef = useRef(onRevealProgress);
  onRevealProgressRef.current = onRevealProgress;

  const parsedContent = useMemo(() => {
    return formatMessage(
      visibleContent,
      citations,
      onCitationClick,
      styles as MessageFormatStyles,
    );
  }, [visibleContent, citations, onCitationClick]);
  const presentation = useMemo(
    () => (isUser ? null : resolveAssistantPresentation(message)),
    [isUser, message],
  );
  const isPrototypeMinimalPresentation = useMemo(
    () =>
      !isUser &&
      Boolean(presentation) &&
      isRecord(message.metadata) &&
      message.metadata.presentationChrome === "prototype-minimal",
    [isUser, message.metadata, presentation],
  );
  const usePrototypeExactPresentation = useMemo(
    () =>
      !isUser &&
      shouldUsePrototypeExactRenderer(presentation, message.metadata),
    [isUser, message.metadata, presentation],
  );
  const renderedPresentation = useMemo(() => {
    if (!presentation) {
      return null;
    }
    if (!usePrototypeExactPresentation) {
      return presentation;
    }
    return {
      ...presentation,
      sections: presentation.sections.filter(
        (section) => section.type !== "sources",
      ),
    };
  }, [presentation, usePrototypeExactPresentation]);
  const presentationOwnsActions = useMemo(
    () =>
      usePrototypeExactPresentation ||
      Boolean(
        renderedPresentation?.sections.some(
          (section) =>
            section.type === "result_cards" ||
            section.type === "related_links" ||
            section.type === "artifacts" ||
            section.type === "task_proposal" ||
            section.type === "task_thread",
        ),
      ),
    [renderedPresentation, usePrototypeExactPresentation],
  );
  const presentationHasTaskProposal = useMemo(
    () =>
      renderedPresentation?.sections.some(
        (section) => section.type === "task_proposal",
      ) === true,
    [renderedPresentation],
  );
  const isStatusReceiptPresentation = useMemo(
    () =>
      !isUser &&
      Boolean(renderedPresentation) &&
      isRecord(message.metadata) &&
      message.metadata.statusReceipt === true &&
      renderedPresentation?.sections.some(
        (section) => section.type === "confirmation",
      ) === true,
    [isUser, message.metadata, renderedPresentation],
  );
  const textRevealConfig: ResolvedAssistantTextRevealConfig | null = useMemo(
    () =>
      resolveAssistantTextRevealConfig({
        metadata: message.metadata,
        responseKind: presentation?.responseKind ?? null,
      }),
    [message.metadata, presentation?.responseKind],
  );
  const revealMessageId = useMemo(
    () => resolveTextRevealMessageId(message.messageId),
    [message.messageId],
  );
  const revealMessageIdRef = useRef<string | null>(revealMessageId);
  const shouldAnimateThisMountRef = useRef(
    revealMessageId ? !settledTextRevealMessageIds.has(revealMessageId) : true,
  );
  if (revealMessageIdRef.current !== revealMessageId) {
    revealMessageIdRef.current = revealMessageId;
    shouldAnimateThisMountRef.current = revealMessageId
      ? !settledTextRevealMessageIds.has(revealMessageId)
      : true;
    revealStartedRef.current = false;
  }
  const revealTargetText = useMemo(() => {
    const presentationText =
      collectAssistantPresentationRevealText(presentation);
    return (presentationText || visibleContent || "").trim();
  }, [visibleContent, presentation]);
  const revealTokens = useMemo(
    () => tokenizeRevealText(revealTargetText),
    [revealTargetText],
  );
  const revealStartDelayMs = textRevealConfig?.startDelayMs ?? 180;
  const revealWordDelayMs = textRevealConfig?.wordDelayMs ?? 34;
  const revealHandoffDelayMs =
    textRevealConfig?.structuredHandoffDelayMs ?? 120;
  const revealStructuredFadeMs = textRevealConfig?.structuredFadeMs ?? 160;
  const shouldRevealText =
    !isUser &&
    !isPending &&
    (!reducedMotion || textRevealConfig?.reducedMotionBehavior === "animate") &&
    shouldAnimateThisMountRef.current &&
    Boolean(textRevealConfig) &&
    revealTokens.length > 0;
  const revealedText = useMemo(
    () => revealTokens.slice(0, revealedTokenCount).join(""),
    [revealTokens, revealedTokenCount],
  );
  const revealedContent = useMemo(
    () =>
      formatMessage(
        revealedText,
        citations,
        onCitationClick,
        styles as MessageFormatStyles,
      ),
    [citations, onCitationClick, revealedText],
  );
  const isRevealPendingText = shouldRevealText && !revealStartedRef.current;
  const isRevealingText =
    shouldRevealText &&
    (revealPhase === "revealing" ||
      revealPhase === "handoff" ||
      isRevealPendingText);
  const isStructuredHandoff = shouldRevealText && revealPhase === "handoff";
  const presentationHasStructuredSections =
    hasStructuredPresentationSections(renderedPresentation);
  const useStableStructuredFrame =
    !isUser &&
    (presentationHasStructuredSections ||
      (isRevealingText && Boolean(renderedPresentation)));
  const shouldRevealInsideStructuredPresentation =
    Boolean(renderedPresentation) &&
    !usePrototypeExactPresentation &&
    isRevealingText;
  const markRevealSettled = () => {
    if (revealMessageId) {
      settledTextRevealMessageIds.add(revealMessageId);
    }
  };

  useEffect(() => {
    if (revealTimerRef.current !== null) {
      window.clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }

    if (!shouldRevealText) {
      setRevealPhase("settled");
      setRevealedTokenCount(revealTokens.length);
      return undefined;
    }

    let cancelled = false;
    let nextTokenIndex = Math.min(1, revealTokens.length);
    revealStartedRef.current = true;
    setRevealPhase("revealing");
    setRevealedTokenCount(nextTokenIndex);
    if (nextTokenIndex >= revealTokens.length) {
      markRevealSettled();
      if (revealHandoffDelayMs > 0) {
        setRevealPhase("handoff");
        revealTimerRef.current = window.setTimeout(() => {
          if (!cancelled) {
            setRevealPhase("settled");
          }
          revealTimerRef.current = null;
        }, revealHandoffDelayMs);
      } else {
        setRevealPhase("settled");
      }
      return undefined;
    }

    const tick = () => {
      if (cancelled) {
        return;
      }
      nextTokenIndex += 1;
      setRevealedTokenCount(nextTokenIndex);
      onRevealProgressRef.current?.();
      if (nextTokenIndex >= revealTokens.length) {
        markRevealSettled();
        if (revealHandoffDelayMs > 0) {
          setRevealPhase("handoff");
          revealTimerRef.current = window.setTimeout(() => {
            if (!cancelled) {
              setRevealPhase("settled");
            }
            revealTimerRef.current = null;
          }, revealHandoffDelayMs);
        } else {
          setRevealPhase("settled");
          revealTimerRef.current = null;
        }
        return;
      }
      revealTimerRef.current = window.setTimeout(tick, revealWordDelayMs);
    };

    revealTimerRef.current = window.setTimeout(tick, revealStartDelayMs);

    return () => {
      cancelled = true;
      revealStartedRef.current = false;
      if (revealTimerRef.current !== null) {
        window.clearTimeout(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    };
  }, [
    revealHandoffDelayMs,
    revealMessageId,
    revealStartDelayMs,
    revealTokens,
    revealWordDelayMs,
    shouldRevealText,
  ]);
  const hasRevealedAnswerText = revealedText.trim().length > 0;
  const contextualActions = useMemo(() => {
    if (presentationHasTaskProposal) {
      return [];
    }
    return presentationOwnsActions
      ? actions.filter((action) => action.type === "draft")
      : actions;
  }, [actions, presentationHasTaskProposal, presentationOwnsActions]);
  const shouldShowContextualActions =
    contextualActions.length > 0 &&
    (!isRevealingText ||
      hasRevealedAnswerText ||
      Boolean(renderedPresentation));

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(visibleContent);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = visibleContent;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.warn("[AI Assistant] Copy failed", error);
    }
  };

  const handleFeedback = (value: AssistantFeedback) => {
    if (!message.messageId) {
      return;
    }
    if (value === "up") {
      setFeedbackBurstKey((current) => current + 1);
    }
    onFeedback?.(message.messageId, value);
  };

  const handleRetry = () => {
    if (!message.messageId) {
      return;
    }
    onRetry?.(message.messageId);
  };

  const resolveCitationLabel = (citation: TCitation, index: number) =>
    getCitationLabel
      ? getCitationLabel(citation, index)
      : `Source ${index + 1}`;

  const resolveCitationTooltip = (citation: TCitation, index: number) =>
    getCitationTooltip ? getCitationTooltip(citation, index) : undefined;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: isPending ? 0.7 : 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={
        isUser
          ? styles.userMessageContainer
          : isPrototypeMinimalPresentation || useStableStructuredFrame
            ? `${styles.assistantMessageContainer} ${styles.assistantMessageContainerFullWidth}`
            : styles.assistantMessageContainer
      }
    >
      <Paper
        className={
          isUser
            ? styles.userMessage
            : isPrototypeMinimalPresentation
              ? styles.assistantMessageMinimal
              : useStableStructuredFrame
                ? `${styles.assistantMessage} ${styles.assistantMessageFullWidth}`
                : styles.assistantMessage
        }
        p={isPrototypeMinimalPresentation ? 0 : "sm"}
        radius={isPrototypeMinimalPresentation ? 0 : "md"}
        data-testid={`chat-message-${role}`}
        data-message-role={role}
        data-message-id={message.messageId ?? undefined}
      >
        <Stack gap="xs">
          {renderedPresentation ? (
            <div
              className={styles.messageStructuredContent}
              style={
                {
                  "--ai-response-structured-fade-ms": `${revealStructuredFadeMs}ms`,
                } as CSSProperties
              }
              data-testid="ai-message-structured-content"
            >
              {usePrototypeExactPresentation ? (
                <PrototypeMinimalPresentationRenderer
                  presentation={renderedPresentation}
                />
              ) : (
                <AssistantPresentationRenderer
                  presentation={renderedPresentation}
                  textRevealState={
                    shouldRevealInsideStructuredPresentation
                      ? {
                          active: true,
                          content: revealedContent,
                          phase: isStructuredHandoff ? "handoff" : "revealing",
                          showCursor: Boolean(textRevealConfig?.cursor),
                        }
                      : null
                  }
                  onSourceClick={
                    onCitationClick
                      ? (_source, index) => {
                          const citation = citations[index];
                          if (citation) {
                            onCitationClick(citation);
                          }
                        }
                      : undefined
                  }
                  getSourceTooltip={
                    getCitationTooltip
                      ? (_source, index) => {
                          const citation = citations[index];
                          return citation
                            ? resolveCitationTooltip(citation, index)
                            : undefined;
                        }
                      : undefined
                  }
                  onTaskProposalStart={onTaskProposalStart}
                  onTaskProposalAskInChat={onTaskProposalAskInChat}
                  onTaskProposalDismiss={onTaskProposalDismiss}
                  activeRouteContext={activeRouteContext}
                  onAssistantPolicyEvent={onAssistantPolicyEvent}
                  onOpenArtifactInGoogleSheets={onOpenArtifactInGoogleSheets}
                />
              )}
            </div>
          ) : isRevealingText ? (
            <div
              className={styles.messageRevealContent}
              data-testid="ai-message-streaming-text"
              data-stream-state={isStructuredHandoff ? "handoff" : "revealing"}
            >
              {revealedContent}
              {textRevealConfig?.cursor ? (
                <span
                  className={styles.messageRevealCursor}
                  data-testid="ai-message-streaming-cursor"
                  aria-hidden="true"
                />
              ) : null}
            </div>
          ) : revealPhase === "settled" && shouldRevealText ? (
            <div
              className={styles.messageContent}
              data-testid={`ai-message-content-${role}`}
            >
              {revealedContent}
            </div>
          ) : (
            <div
              className={styles.messageContent}
              data-testid={`ai-message-content-${role}`}
            >
              {parsedContent}
            </div>
          )}

          {!isUser &&
          !presentation &&
          revealPhase === "settled" &&
          citations.length > 0 ? (
            <Group
              gap="xs"
              mt="xs"
              data-testid="ai-assistant-message-citations"
            >
              {citations.map((citation, index) => {
                const label = resolveCitationLabel(citation, index);
                const tooltip = resolveCitationTooltip(citation, index);
                const badge = (
                  <Badge
                    className={styles.citationBadge}
                    variant="light"
                    color="blue"
                    size="sm"
                    leftSection={<AssistantFileTextIcon size={12} />}
                    style={{ cursor: onCitationClick ? "pointer" : "default" }}
                    onClick={() => onCitationClick?.(citation)}
                    data-testid={`source-badge-${index + 1}`}
                  >
                    {label}
                  </Badge>
                );
                if (!tooltip) {
                  return <span key={`${label}-${index}`}>{badge}</span>;
                }
                return (
                  <Tooltip
                    key={`${label}-${index}`}
                    label={tooltip}
                    multiline
                    w={250}
                  >
                    {badge}
                  </Tooltip>
                );
              })}
            </Group>
          ) : null}
        </Stack>
      </Paper>

      {!isUser && !isPrototypeMinimalPresentation ? (
        <div
          className={`${styles.responseMeta} ${isPrototypeMinimalPresentation ? styles.responseMetaMinimal : ""}`}
          data-testid="ai-assistant-message-meta"
        >
          <div
            className={styles.responseActions}
            data-testid="ai-assistant-response-actions"
          >
            {showCopy ? (
              <Tooltip
                label="Copy"
                withArrow
                position="bottom"
                color="dark"
                transitionProps={{ transition: "fade", duration: 120 }}
              >
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  className={styles.responseAction}
                  onClick={handleCopy}
                  data-testid="ai-action-copy"
                  data-copied={copied}
                  aria-label="Copy"
                >
                  <AssistantCopyIcon size={16} />
                </ActionIcon>
              </Tooltip>
            ) : null}
            {showFeedback && !isStatusReceiptPresentation ? (
              <>
                <Tooltip
                  label="Thumbs up"
                  withArrow
                  position="bottom"
                  color="dark"
                  transitionProps={{ transition: "fade", duration: 120 }}
                >
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    className={styles.responseAction}
                    onClick={() => handleFeedback("up")}
                    data-testid="ai-action-thumbsup"
                    aria-pressed={feedback === "up"}
                    aria-label="Thumbs up"
                  >
                    <motion.span
                      key={`thumb-up-${feedbackBurstKey}`}
                      initial={false}
                      animate={
                        feedbackBurstKey > 0 && !reducedMotion
                          ? {
                              scale: [1, 1.35, 0.96, 1],
                              rotate: [0, -8, 5, 0],
                            }
                          : { scale: 1, rotate: 0 }
                      }
                      transition={{ duration: 0.42, ease: "easeOut" }}
                      style={{ display: "inline-flex" }}
                      data-testid="ai-action-thumbsup-icon"
                    >
                      <AssistantThumbUpIcon size={16} />
                    </motion.span>
                  </ActionIcon>
                </Tooltip>
                <Tooltip
                  label="Thumbs down"
                  withArrow
                  position="bottom"
                  color="dark"
                  transitionProps={{ transition: "fade", duration: 120 }}
                >
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    className={styles.responseAction}
                    onClick={() => handleFeedback("down")}
                    data-testid="ai-action-thumbsdown"
                    aria-pressed={feedback === "down"}
                    aria-label="Thumbs down"
                  >
                    <AssistantThumbDownIcon size={16} />
                  </ActionIcon>
                </Tooltip>
              </>
            ) : null}
            {showRetry && !isStatusReceiptPresentation ? (
              <Tooltip
                label="Retry"
                withArrow
                position="bottom"
                color="dark"
                transitionProps={{ transition: "fade", duration: 120 }}
              >
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  className={styles.responseAction}
                  onClick={handleRetry}
                  disabled={!allowRetry}
                  data-testid="ai-action-retry"
                  aria-label="Retry"
                >
                  <AssistantRefreshIcon size={16} />
                </ActionIcon>
              </Tooltip>
            ) : null}
          </div>

          {shouldShowContextualActions ? (
            <div
              className={styles.contextualActions}
              data-testid="ai-assistant-contextual-actions"
            >
              {contextualActions.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  className={[
                    styles.contextualAction,
                    "chat-button-actions",
                  ].join(" ")}
                  data-testid="ai-context-action"
                  data-action-id={action.id}
                  onClick={() => onAction?.(action)}
                >
                  {action.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </motion.div>
  );
}
